// src/components/slides/SlidesSettings.js
import React, { useState, useEffect } from 'react';
import {
    Settings as SettingsIcon, Monitor, Users, MessageSquare, Shield, Palette,
    Layout, Save, X, Info, BookOpen, Search, Presentation, Sliders, Volume2,
    Eye, BarChart2, CheckSquare, Loader2
} from 'lucide-react';
import { SettingsModal } from '../settings/shared/SettingsModal';
import { TabNavigation } from '../settings/shared/TabNavigation';
import { BasicInfoTab } from '../settings/shared/BasicInfoTab';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { PrivacySection } from '../settings/shared/PrivacySection';
import { SEOSection } from '../settings/shared/SEOSection';
import { firestoreService } from '../../services/firestoreService';

const SlidesSettings = ({ book, bookId, onUpdate, onClose }) => {
    const [activeTab, setActiveTab] = useState('basic');
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [errors, setErrors] = useState({});
    const [uploadStates, setUploadStates] = useState({});

    const tabs = [
        { id: 'basic', label: 'Basic Info', icon: BookOpen },
        { id: 'slides', label: 'Slides Settings', icon: Presentation },
        { id: 'interactions', label: 'Interactions', icon: MessageSquare },
        { id: 'design', label: 'Design & Styling', icon: Palette },
        { id: 'header', label: 'Header', icon: Layout },
        { id: 'privacy', label: 'Privacy & Access', icon: Shield },
        { id: 'seo', label: 'SEO & Metadata', icon: Search }
    ];

    useEffect(() => {
        initializeFormData();
    }, [book]);

    const initializeFormData = () => {
        setFormData({
            // Basic Info
            botName: book?.botName || '',
            description: book?.description || '',
            botCategory: book?.botCategory || '',
            logoUrl: book?.logoUrl || '',
            coverImageUrl: book?.coverImageUrl || '',

            // Publication
            published: book?.published !== false,

            // Access & Privacy
            accessLevel: book?.accessLevel || 'public',
            requireLogin: book?.requireLogin || false,
            allowIndexing: book?.allowIndexing !== false,

            // Styling
            primaryColor: book?.styling?.primaryColor || '#6366f1',
            backgroundColor: book?.styling?.backgroundColor || '#ffffff',
            headerColor: book?.styling?.headerColor || '#ffffff',
            headerTextColor: book?.styling?.headerTextColor || '#333333',
            headerOpacity: book?.styling?.headerOpacity || '100',
            textColor: book?.styling?.textColor || '#1f2937',
            accentColor: book?.styling?.accentColor || '#f59e0b',
            fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif',
            headerRadius: book?.styling?.headerRadius || '0px',
            logoInvert: book.styling?.logoInvert || 0,
            cardRadius: book?.styling?.cardRadius || '12px',
            shadowLevel: book?.styling?.shadowLevel || 'medium',

            // Header Settings
            headerSettings: {
                showHeader: book?.headerSettings?.showHeader ?? true,
                showLogo: book?.headerSettings?.showLogo ?? true,
                showTitle: book?.headerSettings?.showTitle ?? true,
                showDescription: book?.headerSettings?.showDescription ?? false,
                showShareButton: book?.headerSettings?.showShareButton ?? true,
                headerHeight: book?.headerSettings?.headerHeight || 80,
                logoHeight: book?.headerSettings?.logoHeight || 40,
                position: book?.headerSettings?.position || 'sticky',
                layout: book?.headerSettings?.layout || 'default',
                ...book?.headerSettings
            },

            // Slides-Specific Settings
            slidesSettings: {
                // Presentation Behavior
                syncViewers: book?.slidesSettings?.syncViewers ?? true,
                autoAdvance: book?.slidesSettings?.autoAdvance ?? false,
                autoAdvanceDelay: book?.slidesSettings?.autoAdvanceDelay || 5000,
                showSlideNumbers: book?.slidesSettings?.showSlideNumbers ?? true,
                showProgressBar: book?.slidesSettings?.showProgressBar ?? true,
                enableKeyboardNav: book?.slidesSettings?.enableKeyboardNav ?? true,
                enableSwipeNav: book?.slidesSettings?.enableSwipeNav ?? true,

                // Display
                theme: book?.slidesSettings?.theme || 'modern',
                aspectRatio: book?.slidesSettings?.aspectRatio || '16:9',
                transitionEffect: book?.slidesSettings?.transitionEffect || 'fade',
                transitionDuration: book?.slidesSettings?.transitionDuration || 300,

                // Interactions
                allowOfflineInteractions: book?.slidesSettings?.allowOfflineInteractions ?? false,
                allowQuestions: book?.slidesSettings?.allowQuestions ?? true,
                questionLimit: book?.slidesSettings?.questionLimit || 3,
                showPolls: book?.slidesSettings?.showPolls ?? true,
                showMCQResults: book?.slidesSettings?.showMCQResults ?? true,
                anonymousResponses: book?.slidesSettings?.anonymousResponses ?? false,

                // Audience Access
                allowCloudSlides: book?.slidesSettings?.allowCloudSlides ?? true,

                // Branding
                branding: {
                    primaryColor: book?.slidesSettings?.branding?.primaryColor || '#6366f1',
                    logoUrl: book?.slidesSettings?.branding?.logoUrl || ''
                }
            },

            // SEO
            seoTitle: book?.seoTitle || book?.botName || '',
            seoDescription: book?.seoDescription || book?.description || '',
            seoKeywords: book?.seoKeywords || [],
            ogImage: book?.ogImage || book?.coverImageUrl || '',
            favicon: book?.favicon || '',
            headerBackgroundUrl: book?.headerBackgroundUrl || '',

            // Custom Domain
            customDomain: book?.customDomain || '',
            customPath: book?.customPath || '',
        });
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => {
            const newData = { ...prev };
            const keys = field.split('.');
            let current = newData;

            for (let i = 0; i < keys.length - 1; i++) {
                if (!current[keys[i]]) current[keys[i]] = {};
                current = current[keys[i]];
            }

            current[keys[keys.length - 1]] = value;
            return newData;
        });
        setHasChanges(true);
    };

    const handleFileUpload = async (file, fileType) => {
        if (!file) return;

        setUploadStates(prev => ({ ...prev, [fileType]: { uploading: true, error: null } }));

        try {
            const result = await firestoreService.uploadFile(file, `pb-${book.id}/slides/${fileType}`);

            setFormData(prev => ({
                ...prev,
                [fileType === 'logo' ? 'logoUrl' :
                    fileType === 'favicon' ? 'faviconUrl' :
                        fileType === 'headerBackground' ? 'headerBackgroundUrl' :
                            fileType]: result.url
            }));

            setUploadStates(prev => ({ ...prev, [fileType]: { uploading: false, error: null } }));
            setHasChanges(true);

        } catch (error) {
            console.error(`Error uploading ${fileType}:`, error);
            setUploadStates(prev => ({
                ...prev,
                [fileType]: { uploading: false, error: error.message }
            }));
        }
    };

    const handleSave = async () => {
        if (!book) return;

        try {
            setSaving(true);
            setErrors({});

            const updatedBook = {
                ...book,
                botName: formData.botName,
                description: formData.description,
                botCategory: formData.botCategory,
                logoUrl: formData.logoUrl,
                coverImageUrl: formData.coverImageUrl,
                headerBackgroundUrl: formData.headerBackgroundUrl,
                favicon: formData.favicon,
                ogImage: formData.ogImage,

                published: formData.published,
                accessLevel: formData.accessLevel,
                requireLogin: formData.requireLogin,
                allowIndexing: formData.allowIndexing,

                styling: {
                    primaryColor: formData.primaryColor,
                    backgroundColor: formData.backgroundColor,
                    headerColor: formData.headerColor,
                    headerTextColor: formData.headerTextColor,
                    headerOpacity: formData.headerOpacity,
                    textColor: formData.textColor,
                    accentColor: formData.accentColor,
                    fontFamily: formData.fontFamily,
                    headerRadius: formData.headerRadius,
                    logoInvert: formData.logoInvert,
                    cardRadius: formData.cardRadius,
                    shadowLevel: formData.shadowLevel
                },

                headerSettings: formData.headerSettings,
                slidesSettings: formData.slidesSettings,

                seoTitle: formData.seoTitle,
                seoDescription: formData.seoDescription,
                seoKeywords: formData.seoKeywords,

                // Custom Domain
                customDomain: formData.customDomain,
                customPath: formData.customPath,

                updatedAt: new Date()
            };

            await firestoreService.updateBook(book.id, updatedBook);
            if (onUpdate) onUpdate(updatedBook);
            setHasChanges(false);
            onClose();
        } catch (error) {
            console.error('Error saving slides settings:', error);
            setErrors({ general: 'Failed to save settings. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'basic':
                return (
                    <BasicInfoTab
                        formData={formData}
                        onInputChange={handleInputChange}
                        onFileUpload={handleFileUpload}
                        uploadStates={uploadStates}
                        errors={errors}
                        bookId={bookId}
                        templateType="slides"
                    />
                );

            case 'slides':
                return <SlidesSpecificSettings formData={formData} onInputChange={handleInputChange} />;

            case 'interactions':
                return <InteractionSettings formData={formData} onInputChange={handleInputChange} />;

            case 'design':
                return (
                    <DesignBrandingSection
                        formData={formData}
                        onInputChange={handleInputChange}
                        bookId={bookId}
                    />
                );

            case 'header':
                return (
                    <HeaderSettings
                        formData={formData}
                        onInputChange={handleInputChange}
                        onImageUpload={handleFileUpload}
                    />
                );

            case 'privacy':
                return (
                    <PrivacySection
                        bookId={bookId}
                        formData={formData}
                        onInputChange={handleInputChange}
                    />
                );

            case 'seo':
                return (
                    <SEOSection
                        formData={formData}
                        onInputChange={handleInputChange}
                        onImageUpload={handleFileUpload}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <SettingsModal
            isOpen={true}
            onClose={onClose}
            onSave={handleSave}
            title="Slides Settings"
            saving={saving}
            hasChanges={hasChanges}
            headerColor="#6366f1"
            headerIcon={Presentation}
        >
            <TabNavigation
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                hasChanges={hasChanges}
            />
            <div className="flex-1 overflow-y-auto p-6 bg-white">
                {errors.general && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        {errors.general}
                    </div>
                )}
                {renderTabContent()}
            </div>
        </SettingsModal>
    );
};

// =============================================================================
// SLIDES SPECIFIC SETTINGS TAB
// =============================================================================

const SlidesSpecificSettings = ({ formData, onInputChange }) => {
    const settings = formData.slidesSettings || {};

    return (
        <div className="space-y-8">
            {/* Presentation Display */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Monitor className="h-5 w-5 text-indigo-600" />
                    Presentation Display
                </h3>

                <div className="space-y-4">
                    {/* Theme Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Presentation Theme</label>
                        <div className="grid grid-cols-3 gap-4">
                            {[
                                { id: 'modern', label: 'Modern', desc: 'Clean & minimal' },
                                { id: 'dark', label: 'Dark', desc: 'Dark mode' },
                                { id: 'classic', label: 'Classic', desc: 'Traditional style' }
                            ].map(theme => (
                                <button
                                    key={theme.id}
                                    onClick={() => onInputChange('slidesSettings.theme', theme.id)}
                                    className={`p-4 rounded-xl border-2 text-center transition-all ${settings.theme === theme.id
                                        ? 'border-indigo-600 bg-indigo-50 text-indigo-600'
                                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    <div className="font-medium">{theme.label}</div>
                                    <div className="text-xs mt-1 opacity-70">{theme.desc}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Aspect Ratio */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Aspect Ratio</label>
                        <div className="grid grid-cols-3 gap-4">
                            {['16:9', '4:3', '1:1'].map(ratio => (
                                <button
                                    key={ratio}
                                    onClick={() => onInputChange('slidesSettings.aspectRatio', ratio)}
                                    className={`p-3 rounded-lg border-2 text-center font-mono transition-all ${settings.aspectRatio === ratio
                                        ? 'border-indigo-600 bg-indigo-50 text-indigo-600'
                                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                                        }`}
                                >
                                    {ratio}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Transition Effect */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Transition Effect</label>
                        <select
                            value={settings.transitionEffect || 'fade'}
                            onChange={(e) => onInputChange('slidesSettings.transitionEffect', e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            <option value="fade">Fade</option>
                            <option value="slide">Slide</option>
                            <option value="zoom">Zoom</option>
                            <option value="none">None</option>
                        </select>
                    </div>

                    {/* Transition Duration */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Transition Duration: {settings.transitionDuration || 300}ms
                        </label>
                        <input
                            type="range"
                            min="100"
                            max="1000"
                            step="50"
                            value={settings.transitionDuration || 300}
                            onChange={(e) => onInputChange('slidesSettings.transitionDuration', parseInt(e.target.value))}
                            className="w-full accent-indigo-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Fast</span>
                            <span>Slow</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Options */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Sliders className="h-5 w-5 text-indigo-600" />
                    Navigation Options
                </h3>

                <div className="space-y-3">
                    <ToggleSetting
                        label="Show Slide Numbers"
                        description="Display current slide number and total count"
                        checked={settings.showSlideNumbers ?? true}
                        onChange={(val) => onInputChange('slidesSettings.showSlideNumbers', val)}
                    />

                    <ToggleSetting
                        label="Show Progress Bar"
                        description="Display presentation progress at the bottom"
                        checked={settings.showProgressBar ?? true}
                        onChange={(val) => onInputChange('slidesSettings.showProgressBar', val)}
                    />

                    <ToggleSetting
                        label="Enable Keyboard Navigation"
                        description="Use arrow keys to navigate slides"
                        checked={settings.enableKeyboardNav ?? true}
                        onChange={(val) => onInputChange('slidesSettings.enableKeyboardNav', val)}
                    />

                    <ToggleSetting
                        label="Enable Swipe Navigation"
                        description="Swipe to navigate on touch devices"
                        checked={settings.enableSwipeNav ?? true}
                        onChange={(val) => onInputChange('slidesSettings.enableSwipeNav', val)}
                    />
                </div>
            </div>

            {/* Auto-Advance Settings */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Eye className="h-5 w-5 text-indigo-600" />
                    Auto-Advance Settings
                </h3>

                <div className="space-y-4">
                    <ToggleSetting
                        label="Auto-Advance Slides"
                        description="Automatically move to the next slide after a delay"
                        checked={settings.autoAdvance ?? false}
                        onChange={(val) => onInputChange('slidesSettings.autoAdvance', val)}
                    />

                    {settings.autoAdvance && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Auto-Advance Delay: {(settings.autoAdvanceDelay || 5000) / 1000}s
                            </label>
                            <input
                                type="range"
                                min="2000"
                                max="30000"
                                step="1000"
                                value={settings.autoAdvanceDelay || 5000}
                                onChange={(e) => onInputChange('slidesSettings.autoAdvanceDelay', parseInt(e.target.value))}
                                className="w-full accent-indigo-600"
                            />
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>2s</span>
                                <span>30s</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Live Session Settings */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5 text-indigo-600" />
                    Live Session Settings
                </h3>

                <div className="space-y-3">
                    <ToggleSetting
                        label="Synchronize Viewers"
                        description="Automatically navigate viewers to the slide you are presenting"
                        checked={settings.syncViewers ?? true}
                        onChange={(val) => onInputChange('slidesSettings.syncViewers', val)}
                    />

                    <ToggleSetting
                        label="Allow Cloud Slides for Audience"
                        description="When disabled, audience must upload their own local copy of slides to reduce bandwidth costs"
                        checked={settings.allowCloudSlides ?? true}
                        onChange={(val) => onInputChange('slidesSettings.allowCloudSlides', val)}
                    />
                </div>
            </div>
        </div>
    );
};

// =============================================================================
// INTERACTION SETTINGS TAB
// =============================================================================

const InteractionSettings = ({ formData, onInputChange }) => {
    const settings = formData.slidesSettings || {};

    return (
        <div className="space-y-8">
            {/* Questions Settings */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-purple-600" />
                    Audience Questions
                </h3>

                <div className="space-y-4">
                    <ToggleSetting
                        label="Allow Offline Interactions"
                        description="Let owners add interactions to slides outside of live sessions"
                        checked={settings.allowOfflineInteractions ?? false}
                        onChange={(val) => onInputChange('slidesSettings.allowOfflineInteractions', val)}
                        color="purple"
                    />

                    <ToggleSetting
                        label="Allow Questions"
                        description="Viewers can submit questions during the live session"
                        checked={settings.allowQuestions ?? true}
                        onChange={(val) => onInputChange('slidesSettings.allowQuestions', val)}
                        color="purple"
                    />

                    {settings.allowQuestions && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Questions Limit per User: {settings.questionLimit || 3}
                            </label>
                            <div className="flex items-center gap-4">
                                <input
                                    type="range"
                                    min="1"
                                    max="10"
                                    value={settings.questionLimit || 3}
                                    onChange={(e) => onInputChange('slidesSettings.questionLimit', parseInt(e.target.value))}
                                    className="flex-1 accent-purple-600"
                                />
                                <span className="bg-purple-100 text-purple-600 px-3 py-1 rounded-full font-bold min-w-[40px] text-center">
                                    {settings.questionLimit || 3}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Polls & MCQ Settings */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <BarChart2 className="h-5 w-5 text-blue-600" />
                    Polls & Multiple Choice
                </h3>

                <div className="space-y-3">
                    <ToggleSetting
                        label="Show Poll Results"
                        description="Display real-time poll results to all viewers"
                        checked={settings.showPolls ?? true}
                        onChange={(val) => onInputChange('slidesSettings.showPolls', val)}
                        color="blue"
                    />

                    <ToggleSetting
                        label="Show MCQ Results"
                        description="Display correct/incorrect answers after submission"
                        checked={settings.showMCQResults ?? true}
                        onChange={(val) => onInputChange('slidesSettings.showMCQResults', val)}
                        color="green"
                    />

                    <ToggleSetting
                        label="Anonymous Responses"
                        description="Hide respondent identities in results"
                        checked={settings.anonymousResponses ?? false}
                        onChange={(val) => onInputChange('slidesSettings.anonymousResponses', val)}
                        color="gray"
                    />
                </div>
            </div>

            {/* Branding */}
            <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Palette className="h-5 w-5 text-indigo-600" />
                    Interaction Branding
                </h3>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Primary Accent Color</label>
                    <div className="flex items-center gap-4">
                        <input
                            type="color"
                            value={settings.branding?.primaryColor || '#6366f1'}
                            onChange={(e) => onInputChange('slidesSettings.branding.primaryColor', e.target.value)}
                            className="w-12 h-12 rounded cursor-pointer border-0"
                        />
                        <input
                            type="text"
                            value={settings.branding?.primaryColor || '#6366f1'}
                            onChange={(e) => onInputChange('slidesSettings.branding.primaryColor', e.target.value)}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-mono"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

// =============================================================================
// TOGGLE SETTING COMPONENT
// =============================================================================

const ToggleSetting = ({ label, description, checked, onChange, color = 'indigo' }) => {
    const colorClasses = {
        indigo: 'bg-indigo-600',
        purple: 'bg-purple-600',
        blue: 'bg-blue-600',
        green: 'bg-green-600',
        gray: 'bg-gray-600'
    };

    return (
        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 cursor-pointer transition-colors">
            <div>
                <h4 className="font-medium text-gray-900">{label}</h4>
                <p className="text-xs text-gray-500">{description}</p>
            </div>
            <button
                type="button"
                onClick={() => onChange(!checked)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? colorClasses[color] : 'bg-gray-300'}`}
            >
                <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`}
                />
            </button>
        </label>
    );
};

export default SlidesSettings;
